<script setup lang="ts">
import { X } from 'lucide-vue-next'
import { onMounted, ref, watch } from 'vue'
import Word from '@/assets/docs.png'
import Pdf from '@/assets/pdf.jpg'
import Video from '@/assets/video.jpg'
import Xlsx from '@/assets/xlsx.png'
import { useAuth } from '@/composables'
import { Card, CardContent, CardFooter } from '@/foundation/card'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/foundation/carousel'
import { Skeleton } from '@/foundation/skeleton'
import { useToast } from '@/foundation/toast/use-toast'
import { useAccountStore } from '@/stores/account'
import FilePreview from './FilePreview.vue'

const { toast } = useToast()

const props = withDefaults(
  defineProps<{
    files: any[]
    showLabel: boolean
    localImage: boolean
  }>(),
  {
    showLabel: true,
    localImage: false,
  },
)

const emits = defineEmits<{
  (e: 'remove', fileIdOrIndex: string | number): void
}>()

const { getToken } = useAuth()
const accountStore = useAccountStore()
const token = ref()

const imageLoaded = ref<Record<string, boolean>>({})

watch(
  () => props.files,
  (newFiles) => {
    newFiles.forEach((file) => {
      const key = file.id || file.fileName

      if (imageLoaded.value[key] === undefined) {
        imageLoaded.value[key] = false
      }
    })
    Object.keys(imageLoaded.value).forEach((key) => {
      if (!newFiles.find((file) => (file.id || file.fileName) === key)) {
        delete imageLoaded.value[key]
      }
    })
  },
  { deep: true, immediate: true },
)

function handleRemove(file: any, index: number) {
  if (!file.createdBy) {
    emits('remove', index)

    return
  }
  if (file.createdBy !== accountStore.user.id) {
    const errorMessage = 'You are not authorized to delete this file.'

    toast({
      title: errorMessage,
      variant: 'destructive',
    })

    return
  }
  const fileIdOrIndex = file.id ?? index

  emits('remove', fileIdOrIndex)
}

const selectedFile = ref(null)
const previewDialogOpen = ref(false)
const url = ref('')

const handlePreview = (file: any) => {
  if (props.localImage && file.file) {
    url.value = URL.createObjectURL(file.file)
  } else if (file.id) {
    url.value = `${import.meta.env.VITE_API_BASE_URL}/download/${file.id}?access_token=${token.value}`
  }
  selectedFile.value = file
  previewDialogOpen.value = true
}

const previewUrl = (file: any) => {
  const imageMimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']

  if (!file.mimetype || !imageMimeTypes.includes(file.mimetype)) {
    return getIconByMimeType(file.mimetype)
  }

  if (props.localImage) {
    return URL.createObjectURL(file.file)
  }

  return file.id
    ? `${import.meta.env.VITE_API_BASE_URL}/download/${file.id}?access_token=${token.value}`
    : getIconByMimeType(file.mimetype)
}

function isShowRemoveButton(file: any) {
  if (!file.createdBy) {
    return true
  }

  return file.createdBy === accountStore.user.id
}

function getIconByMimeType(mimeType: string) {
  const mimeTypeIcons: Record<string, any> = {
    'image/jpeg': 'icons/image-icon.png',
    'image/png': 'icons/image-icon.png',
    'image/gif': Video,
    'image/webp': 'icons/image-icon.png',
    'application/pdf': Pdf,
    'application/msword': Word,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': Word,
    'application/vnd.ms-excel': Xlsx,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': Xlsx,
    'video/mp4': Video,
    'video/x-matroska': Video,
  }

  return mimeTypeIcons[mimeType] || 'icons/default-icon.png'
}

function onImageLoad(file: any) {
  const key = file.id || file.fileName

  imageLoaded.value[key] = true
}

onMounted(async () => {
  token.value = await getToken()
})
</script>

<template>
  <h3 v-if="showLabel" class="text-sm font-semibold mb-1">Attachments ({{ props.files.length }})</h3>
  <Carousel class="w-full" :opts="{ align: 'start' }">
    <CarouselContent class="-ml-2">
      <CarouselItem
        v-for="(file, index) in props.files"
        :key="file.id || file.fileName"
        :class="['pl-1', 'md:basis-1/2', localImage ? 'lg:basis-1/6' : 'lg:basis-1/4']"
      >
        <div class="p-1">
          <Card class="cursor-pointer border-border" @click="handlePreview(file)">
            <CardContent class="flex items-center justify-center !p-0 relative group">
              <img
                v-show="imageLoaded[file.id || file.fileName]"
                :src="previewUrl(file)"
                class="w-full h-20 object-cover block transition-all duration-300 rounded-t-sm"
                alt="Preview"
                @load="onImageLoad(file)"
              />
              <Skeleton v-show="!imageLoaded[file.id || file.fileName]" class="w-full h-20" />
              <div
                v-show="imageLoaded[file.id || file.fileName]"
                class="absolute inset-0 bg-gradient-to-b from-transparent to-black/70 transition-all duration-300"
              ></div>
              <button
                v-if="isShowRemoveButton(file)"
                class="absolute top-2 right-2 bg-secondary rounded-lg p-2 shadow transition-opacity hover:bg-slate-200 hover:!text-dark-text"
                aria-label="Supprimer le fichier"
                @click.stop="handleRemove(file, index)"
              >
                <X :size="16" class="text-inherit" />
              </button>
            </CardContent>
            <CardFooter
              class="text-xs !p-1 whitespace-nowrap overflow-hidden text-ellipsis block max-w-[150px]"
              :title="file.fileName"
            >
              <Skeleton v-show="!imageLoaded[file.id || file.fileName]" class="w-full h-4" />
              <div v-show="imageLoaded[file.id || file.fileName]">{{ file.fileName }}</div>
            </CardFooter>
          </Card>
        </div>
      </CarouselItem>
    </CarouselContent>
    <CarouselPrevious />
    <CarouselNext />
  </Carousel>

  <FilePreview v-if="selectedFile" v-model="previewDialogOpen" :file="selectedFile" :url="url" />
</template>
